// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useTranslation } from 'react-i18next';
import type { HistoricalBalances } from '@shared/data-access/types/brokerage-types';
import { SERIES_COLORS } from '@shared/ui-kit';
import { type AreaData } from 'lightweight-charts';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type SeriesDataKeys = 'net_value' | 'investment' | 'cash' | 'debt';

interface SeriesData {
  id: SeriesDataKeys;
  label: string;
  data: AreaData[];
  color: string;
}

export const useHistoricalBalancesChart = (
  data: HistoricalBalances,
  fixedSerie?: SeriesDataKeys,
) => {
  const { t } = useTranslation();
  const series: Record<SeriesDataKeys, SeriesData> = useMemo(
    () => ({
      net_value: {
        id: 'net_value',
        label: t('common.investment-cash', { ns: 'common' }),
        color: SERIES_COLORS[0],
        data: data.net_value.map((value, index) => ({
          time: data.dates[index].split('T')[0],
          value,
        })),
      },
      investment: {
        id: 'investment',
        label: t('common.investment', { ns: 'common' }),
        color: SERIES_COLORS[1],
        data: data.investment.map((value, index) => ({
          time: data.dates[index].split('T')[0],
          value,
        })),
      },
      cash: {
        id: 'cash',
        label: t('aggregator.summary-account-balance', { ns: 'aggregator' }),
        color: SERIES_COLORS[2],
        data: data.cash.map((value, index) => ({
          time: data.dates[index].split('T')[0],
          value,
        })),
      },
      debt: {
        id: 'debt',
        label: t('glossary.debt', { ns: 'glossary' }),
        color: SERIES_COLORS[4],
        data: data.debt.map((value, index) => ({
          time: data.dates[index].split('T')[0],
          value,
        })),
      },
    }),
    [data, t],
  );

  const [activeSerie, setActiveSerie] = useState<SeriesData>(() =>
    fixedSerie ? series[fixedSerie] : series.net_value,
  );
  /* istanbul ignore next */
  const metrics = useMemo(() => {
    const firstValue = activeSerie.data.at(0)?.value ?? 0;
    const lastValue = activeSerie.data.at(-1)?.value ?? 0;
    const difference = lastValue - firstValue;
    const performance = firstValue !== 0 ? difference / firstValue : 0;

    return { lastValue: lastValue, difference, performance };
  }, [activeSerie]);

  const onChangeSerie = useCallback(
    (label: SeriesDataKeys) => setActiveSerie(series[label]),
    [series],
  );

  const selectorOptions = useMemo(
    () =>
      Object.values(series).map(serie => ({
        label: serie.label,
        value: serie.id,
      })),
    [series],
  );

  // Update the new data for current serie
  useEffect(() => {
    setActiveSerie(prevSerie => series[prevSerie.id]);
  }, [series]);

  return {
    series,
    metrics,
    activeSerie,
    onChangeSerie,
    selectorOptions,
    isFixedMode: !!fixedSerie,
  };
};
